// Formatting in the organization's time zone, not the browser's: a follow-up "at 11:00" means 11:00 where the team
// works, wherever the owner opens the dashboard.
import type { AiState, Digest, Direction, Interest, Role, Stage, TaskType } from "./types";

const formats = new Map<string, Intl.DateTimeFormat>();

function dtf(
  tz: string,
  opts: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = tz + JSON.stringify(opts);
  let f = formats.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat("en-GB", { timeZone: tz, ...opts });
    formats.set(key, f);
  }
  return f;
}

interface Parts {
  y: number;
  m: number;
  d: number;
  h: number;
  min: number;
}

export function wallParts(date: Date, tz: string): Parts {
  const p = dtf(tz, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const get = (type: string) => Number(p.find((x) => x.type === type)?.value);
  return {
    y: get("year"),
    m: get("month"),
    d: get("day"),
    h: get("hour"),
    min: get("minute"),
  };
}

const pad = (n: number) => String(n).padStart(2, "0");

/** The local date ("2026-10-01") at an instant in a time zone. */
export function localDate(date: Date, tz: string): string {
  const p = wallParts(date, tz);
  return `${p.y}-${pad(p.m)}-${pad(p.d)}`;
}

/** An instant as the wall time "YYYY-MM-DDTHH:MM" in a time zone (the value of a datetime-local input). */
export function toWallTime(iso: string | Date, tz: string): string {
  const p = wallParts(typeof iso === "string" ? new Date(iso) : iso, tz);
  return `${p.y}-${pad(p.m)}-${pad(p.d)}T${pad(p.h)}:${pad(p.min)}`;
}

/** A wall time "YYYY-MM-DDTHH:MM" in a time zone as an instant. */
export function fromWallTime(local: string, tz: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local);
  if (!m) throw new Error(`Not a date and time: ${local}`);
  const [y, mo, d, h, min] = m.slice(1).map(Number);
  const wanted = Date.UTC(y, mo - 1, d, h, min);
  let guess = wanted;
  for (let i = 0; i < 3; i++) {
    const p = wallParts(new Date(guess), tz);
    const shown = Date.UTC(p.y, p.m - 1, p.d, p.h, p.min);
    if (shown === wanted) break;
    guess -= shown - wanted;
  }
  return new Date(guess);
}

export function fmtTime(iso: string, tz: string): string {
  return dtf(tz, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
    .format(new Date(iso));
}

export function fmtDate(iso: string, tz: string): string {
  return dtf(tz, { weekday: "short", day: "numeric", month: "short" }).format(
    new Date(iso),
  );
}

export function fmtDateTime(iso: string, tz: string): string {
  return `${fmtDate(iso, tz)}, ${fmtTime(iso, tz)}`;
}

/**
 * A moment exactly, for a record that must leave no doubt (D-157): "Wed 7 Oct 2026, 10:59:13 GMT+5:30", in the
 * company's time zone with its offset from GMT.
 */
export function fmtStamp(iso: string, tz: string): string {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: tz,
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
      timeZoneName: "shortOffset",
    }).formatToParts(new Date(iso)).map((p) => [p.type, p.value]),
  );
  const offset = parts.timeZoneName === "GMT" ? "GMT+0" : parts.timeZoneName;
  return `${parts.weekday} ${parts.day} ${parts.month} ${parts.year}, ${parts.hour}:${parts.minute}:${parts.second} ${offset}`;
}

/** How long ago, for something that just came in: "just now", "3 min ago", "5 h ago", then the day and time. */
export function fmtAgo(iso: string, tz: string, now = Date.now()): string {
  const minutes = Math.floor((now - Date.parse(iso)) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 24 * 60) return `${Math.floor(minutes / 60)} h ago`;
  return fmtDateTime(iso, tz);
}

/** A calendar date ("2026-10-01") as "Thu 1 Oct". */
export function fmtDay(day: string): string {
  return dtf("UTC", { weekday: "short", day: "numeric", month: "short" })
    .format(new Date(`${day}T00:00:00Z`));
}

function dayDiff(a: string, b: string): number {
  return Math.round(
    (Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000,
  );
}

/** When a follow-up is due, relative to now: "Overdue 2 h", "Today 15:00", "Tomorrow 11:00", "Fri 11:00". */
export function fmtDue(
  iso: string,
  tz: string,
  now: Date = new Date(),
): string {
  const due = new Date(iso);
  const mins = Math.round((due.getTime() - now.getTime()) / 60_000);
  if (mins < 0) {
    const late = -mins;
    if (late < 60) return `Overdue ${late} min`;
    if (late < 48 * 60) return `Overdue ${Math.floor(late / 60)} h`;
    return `Overdue ${Math.floor(late / 1440)} days`;
  }
  const days = dayDiff(localDate(now, tz), localDate(due, tz));
  const time = fmtTime(iso, tz);
  if (days === 0) return mins < 60 ? `In ${mins} min` : `Today ${time}`;
  if (days === 1) return `Tomorrow ${time}`;
  if (days < 7) return `${dtf(tz, { weekday: "short" }).format(due)} ${time}`;
  return fmtDateTime(iso, tz);
}

export function isOverdue(iso: string, now: Date = new Date()): boolean {
  return new Date(iso).getTime() < now.getTime();
}

/** A call she placed that nobody answered (WEB-09, D-119): the phone logs it as outgoing, with no talk time, and the
 * server keeps it as not answered; so too a call she said did not connect though the phone logged seconds, an
 * operator's message or a voicemail (D-141). A missing duration is not taken for one. */
export function isUnanswered(
  direction: string | null | undefined,
  durationS: number | null | undefined,
  callStatus?: string | null,
): boolean {
  return direction === "outgoing" && (durationS === 0 || callStatus === "not_answered");
}

/** Seconds as "2:05", or "1:02:05" past an hour. */
export function fmtDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds)) return "–";
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rest = s % 60;
  return h > 0 ? `${h}:${pad(m)}:${pad(rest)}` : `${m}:${pad(rest)}`;
}

const INTEREST_RANK: Record<string, number> = { hot: 0, warm: 1, cold: 2 };

/** The brief's Today list is "by time and interest": by due time, and a hotter lead first at the same time (APP-08). */
export function byTimeThenInterest(
  a: { due_at: string; lead?: { interest: string | null } | null },
  b: { due_at: string; lead?: { interest: string | null } | null },
): number {
  const byTime = new Date(a.due_at).getTime() - new Date(b.due_at).getTime();
  if (byTime !== 0) return byTime;
  return (INTEREST_RANK[a.lead?.interest ?? ""] ?? 3) - (INTEREST_RANK[b.lead?.interest ?? ""] ?? 3);
}

/**
 * How a phone's last sync went (DEV-03), for the Team page: the problem its telecaller was shown, or how many
 * recordings are waiting. Null when the phone has not reported yet.
 */
export function syncLine(
  d: { last_sync_at: string | null; sync_problem: string | null; files_waiting: number | null },
  tz: string,
): { text: string; problem: boolean } | null {
  if (!d.last_sync_at) return null;
  const when = `Synced ${fmtDateTime(d.last_sync_at, tz)}`;
  if (d.sync_problem) return { text: `${when}: ${d.sync_problem}`, problem: true };
  const waiting = d.files_waiting ?? 0;
  return {
    text: waiting > 0 ? `${when}, ${waiting} recording${waiting === 1 ? "" : "s"} waiting` : when,
    problem: false,
  };
}

/** +919876543210 as "+91 98765 43210"; other numbers unchanged. */
export function fmtPhone(e164: string | null | undefined): string {
  if (!e164) return "";
  const m = /^\+91(\d{5})(\d{5})$/.exec(e164);
  return m ? `+91 ${m[1]} ${m[2]}` : e164;
}

export function fmtNumber(n: number | null | undefined): string {
  return n == null ? "–" : new Intl.NumberFormat("en-IN").format(n);
}

export const STAGES: Stage[] = [
  "new",
  "contacted",
  "interested",
  "site_visit",
  "negotiating",
  "won",
  "lost",
  "not_a_lead",
];
export const ACTIVE_STAGES: Stage[] = [
  "new",
  "contacted",
  "interested",
  "site_visit",
  "negotiating",
];
export const INTERESTS: Interest[] = ["hot", "warm", "cold", "none"];
export const TASK_TYPES: TaskType[] = [
  "call",
  "site_visit",
  "send_info",
  "whatsapp",
  "email",
  "demo",
];
export const ROLES: Role[] = ["owner", "admin", "manager", "caller"];

export const stageLabel: Record<Stage, string> = {
  new: "New",
  contacted: "Contacted",
  interested: "Interested",
  site_visit: "Site visit",
  negotiating: "Negotiating",
  won: "Won",
  lost: "Lost",
  not_a_lead: "Not a lead",
};

export const interestLabel: Record<Interest, string> = {
  hot: "Hot",
  warm: "Warm",
  cold: "Cold",
  none: "No interest",
};

export const taskTypeLabel: Record<TaskType, string> = {
  call: "Call",
  site_visit: "Site visit",
  send_info: "Send info",
  whatsapp: "WhatsApp",
  email: "Email",
  demo: "Demo",
};

export const roleLabel: Record<Role, string> = {
  owner: "Owner",
  admin: "Admin",
  manager: "Manager",
  caller: "Telecaller",
};

export const directionLabel: Record<Direction, string> = {
  incoming: "Incoming",
  outgoing: "Outgoing",
  missed: "Missed",
  rejected: "Rejected",
  blocked: "Blocked",
  voicemail: "Voicemail",
  unknown: "Unknown",
};

export function taskSourceLabel(source: string, rule: string | null): string {
  if (source === "ai") return "AI, from the call";
  if (source === "rule") {
    if (rule?.startsWith("missed_call")) return "Missed call";
    if (rule?.startsWith("no_answer")) return "No answer";
    if (rule?.startsWith("quiet")) return "Gone quiet";
    return "Follow-up rule";
  }
  if (source === "import") return "Imported";
  if (source === "system") return "Voxowa";
  if (source === "caller") return "Telecaller's choice";
  if (source === "owner") return "Set by a manager";
  return "Added by hand";
}

/** Made by Voxowa itself (its AI from a call, its follow-up rules, an import) rather than by a person, so that a wrong
 * follow-up can be told apart: Voxowa's logic, or a person's choice (WEB-08, D-109). */
export function taskByVoxowa(source: string): boolean {
  return source === "ai" || source === "rule" || source === "system" || source === "import";
}

/** Who made a follow-up, in a sentence for the label's tooltip. */
export function taskSourceTitle(source: string, rule: string | null): string {
  if (source === "ai") return "Made by Voxowa's AI, from what was said on the call";
  if (source === "rule") return `Made by Voxowa's follow-up rules: ${taskSourceLabel(source, rule).toLowerCase()}`;
  if (taskByVoxowa(source)) return "Made by Voxowa";
  if (source === "caller") return "Set by the telecaller";
  if (source === "owner") return "Set by an owner, admin or manager";
  return "Added by a person";
}

/** Who is allowed to see what, mirroring the database's rules for the navigation only. */
export function isManagerRole(role: Role | undefined): boolean {
  return role === "owner" || role === "admin" || role === "manager";
}

export function isAdminRole(role: Role | undefined): boolean {
  return role === "owner" || role === "admin";
}

/**
 * The notice every page shows when AI work is held, and the warning owners and admins get before the month's minutes
 * run out (DASH-04). Null when there is nothing to say. `buy`: offer the link to buy minutes. A company with neither
 * Coach nor Autopilot uses no AI and no minutes, so it hears nothing of either (D-181); only being under review.
 */
export function aiNotice(
  member: {
    role: Role;
    ai_state: AiState;
    minutes: { left: number; included: number } | null;
    features?: { coach: boolean; autopilot: boolean } | null;
  },
): { tone: "warm" | "danger"; text: string; buy: boolean } | null {
  const admin = isAdminRole(member.role);
  const ai = !member.features || member.features.coach || member.features.autopilot;
  if (!ai && member.ai_state !== "under_review") return null;
  if (member.ai_state === "under_review") {
    return {
      tone: "warm",
      text: "Your company is currently under review. Once it is manually reviewed, you will be allowed to use our " +
        "trial. Until then you can invite one phone number, and calls are saved but not analysed.",
      buy: false,
    };
  }
  if (member.ai_state === "paused") {
    return {
      tone: "warm",
      text: "AI is paused for maintenance. Calls and recordings are still saved and will be analysed when it resumes.",
      buy: false,
    };
  }
  if (member.ai_state === "minutes_used_up") {
    return {
      tone: "danger",
      text: admin
        ? "This month's AI minutes are used up. Calls and recordings are still saved; buy minutes to have them analysed now."
        : "This month's AI minutes are used up. Calls are still saved and will be analysed once your manager adds minutes.",
      buy: admin,
    };
  }
  const m = member.minutes;
  if (admin && m && m.included > 0 && m.left <= m.included / 10) {
    return { tone: "warm", text: `Only ${fmtNumber(m.left)} AI minutes left this month.`, buy: true };
  }
  return null;
}

/** How the digest went out by email, in a line; null while it waits for email to be turned on. */
export function deliveryLine(d: Digest, tz: string): string | null {
  switch (d.status) {
    case "sent": {
      const when = d.sent_at ? ` at ${fmtDateTime(d.sent_at, tz)}` : "";
      const who = `${d.recipients ?? 0} ${d.recipients === 1 ? "person" : "people"}`;
      return `Emailed to ${who}${when}.${d.error ? ` ${d.error}` : ""}`;
    }
    case "failed":
      return `The email failed: ${d.error ?? "unknown error"}`;
    case "skipped":
      return `Not emailed: ${d.error ?? "skipped"}`;
    default:
      return null;
  }
}

const rupees = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 });

/** Paise as rupees: 299900 as "₹2,999.00". */
export function fmtRupees(paise: number | null | undefined): string {
  return paise == null ? "–" : rupees.format(paise / 100);
}
